import * as React from "react"
import { useState, useRef, useEffect } from "react"
import { ChevronLeft, ChevronRight, Calendar, Clock, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { parseLocalDate } from "../../utils/dateUtils"

export function CustomDateTimePicker({
  value,
  onChange,
  placeholder = "Select date & time",
  disabled = false,
  min,
  max,
  className,
  label,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedHour, setSelectedHour] = useState(12)
  const [selectedMinute, setSelectedMinute] = useState(0)
  const [step, setStep] = useState('date') // 'date' or 'time'
  const dialogRef = useRef(null)

  // Parse value on mount and when value changes
  useEffect(() => {
    if (value) {
      // Value format: "YYYY-MM-DDTHH:mm" or "YYYY-MM-DD HH:mm"
      const dateTimeParts = value.includes('T') ? value.split('T') : value.split(' ')
      if (dateTimeParts.length >= 1) {
        const datePart = dateTimeParts[0]
        const parsed = parseLocalDate(datePart)
        if (parsed) {
          setSelectedDate(parsed)
          setCurrentMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1))
        }
        if (dateTimeParts.length >= 2) {
          const timePart = dateTimeParts[1]
          const [hours, minutes] = timePart.split(':').map(Number)
          if (!isNaN(hours)) setSelectedHour(hours)
          if (!isNaN(minutes)) setSelectedMinute(minutes)
        }
      }
    } else {
      setSelectedDate(null)
      setSelectedHour(12)
      setSelectedMinute(0)
    }
  }, [value])

  // Handle dialog open/close
  useEffect(() => {
    const dialog = dialogRef.current
    if (isOpen && dialog) {
      dialog.showModal()
      setStep('date')
    } else if (!isOpen && dialog?.open) {
      dialog.close()
    }
    
    // Handle dialog close event (e.g., when pressing Escape)
    const handleClose = () => {
      setIsOpen(false)
    }
    
    dialog?.addEventListener('close', handleClose)
    return () => {
      dialog?.removeEventListener('close', handleClose)
    }
  }, [isOpen])

  const formatDisplayValue = () => {
    if (!value) return ""
    const dateTimeParts = value.includes('T') ? value.split('T') : value.split(' ')
    if (dateTimeParts.length >= 1) {
      const datePart = dateTimeParts[0]
      const parsed = parseLocalDate(datePart)
      if (parsed) {
        const dateStr = parsed.toLocaleDateString('en-IN', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        })
        if (dateTimeParts.length >= 2) {
          const timePart = dateTimeParts[1]
          const [hours, minutes] = timePart.split(':').map(Number)
          const hour12 = hours % 12 || 12
          const ampm = hours >= 12 ? 'PM' : 'AM'
          const minStr = minutes.toString().padStart(2, '0')
          return `${dateStr}, ${hour12}:${minStr} ${ampm}`
        }
        return dateStr
      }
    }
    return value
  }

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDay = firstDay.getDay()
    
    const days = []
    // Add empty slots for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null)
    }
    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i))
    }
    return days
  }

  const isDateDisabled = (date) => {
    if (!date) return true
    
    // Normalize compare date to midnight (remove time component)
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
    
    // Check min date restriction
    if (min) {
      const minStr = min.split('T')[0]
      const [year, month, day] = minStr.split('-').map(Number)
      const minDate = new Date(year, month - 1, day, 0, 0, 0, 0)
      if (compareDate.getTime() < minDate.getTime()) return true
    }
    
    // Check max date restriction
    if (max) {
      const maxStr = max.split('T')[0]
      const [year, month, day] = maxStr.split('-').map(Number)
      const maxDate = new Date(year, month - 1, day, 0, 0, 0, 0)
      if (compareDate.getTime() > maxDate.getTime()) return true
    }
    
    return false
  }

  const handleDateSelect = (date) => {
    if (isDateDisabled(date)) return
    setSelectedDate(date)
    setStep('time')
  }

  const handleTimeConfirm = () => {
    if (selectedDate) {
      const year = selectedDate.getFullYear()
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
      const day = String(selectedDate.getDate()).padStart(2, '0')
      const hours = String(selectedHour).padStart(2, '0')
      const minutes = String(selectedMinute).padStart(2, '0')
      const dateTimeStr = `${year}-${month}-${day}T${hours}:${minutes}`
      
      // Close dialog first, then update value
      if (dialogRef.current?.open) {
        dialogRef.current.close()
      }
      setIsOpen(false)
      
      // Use setTimeout to ensure state updates after dialog closes
      setTimeout(() => {
        onChange?.(dateTimeStr)
      }, 0)
    }
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange?.('')
    setSelectedDate(null)
    setSelectedHour(12)
    setSelectedMinute(0)
  }

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const days = getDaysInMonth(currentMonth)
  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

  const hours = Array.from({ length: 24 }, (_, i) => i)
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5)

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(true)}
        className={cn(
          "flex h-11 w-full items-center justify-between rounded-xl border border-input bg-white px-3 py-2 text-left text-base shadow-sm transition-colors",
          "hover:bg-slate-50 focus:outline-none focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-100",
          value ? "text-slate-900 border-slate-900" : "text-slate-400",
          className
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <span className="truncate">{value ? formatDisplayValue() : placeholder}</span>
        </div>
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 hover:bg-slate-200 rounded-full ml-1"
          >
            <X className="w-3 h-3 text-slate-400" />
          </button>
        )}
      </button>

      {/* Native Dialog Element */}
      <dialog
        ref={dialogRef}
        className="fixed inset-0 m-auto p-0 bg-transparent border-none max-w-sm w-[calc(100%-2rem)] rounded-2xl shadow-2xl backdrop:bg-black/60"
        onClick={(e) => {
          if (e.target === dialogRef.current) {
            setIsOpen(false)
          }
        }}
      >
        <div className="bg-white rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="text-lg font-semibold text-slate-800">
              {step === 'date' ? (label || "Select Date") : "Select Time"}
            </h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-full hover:bg-slate-100 touch-manipulation"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {step === 'date' ? (
            /* Date Selection */
            <div className="p-4">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={goToPrevMonth}
                  className="p-2 rounded-full hover:bg-slate-100 touch-manipulation"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-600" />
                </button>
                <h4 className="text-base font-semibold text-slate-800">
                  {currentMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                </h4>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-2 rounded-full hover:bg-slate-100 touch-manipulation"
                >
                  <ChevronRight className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              {/* Week Days Header */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map((day, idx) => (
                  <div key={idx} className="text-center text-xs font-semibold text-slate-400 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((date, idx) => {
                  if (!date) {
                    return <div key={idx} className="h-10" />
                  }
                  
                  const isSelected = selectedDate && 
                    date.getDate() === selectedDate.getDate() &&
                    date.getMonth() === selectedDate.getMonth() &&
                    date.getFullYear() === selectedDate.getFullYear()
                  
                  const isToday = date.toDateString() === new Date().toDateString()
                  const isDisabled = isDateDisabled(date)

                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => handleDateSelect(date)}
                      className={cn(
                        "h-10 w-full rounded-lg text-sm font-medium transition-all touch-manipulation",
                        isDisabled
                          ? "text-slate-300 cursor-not-allowed bg-slate-50"
                          : isSelected 
                            ? "bg-slate-800 text-white" 
                            : isToday 
                              ? "bg-slate-100 text-slate-800 font-bold"
                              : "hover:bg-slate-100 text-slate-700"
                      )}
                    >
                      {date.getDate()}
                    </button>
                  )
                })}
              </div>
            </div>
          ) : (
            /* Time Selection */
            <div className="p-4">
              {/* Selected Date Display */}
              <div className="text-center mb-4 p-3 bg-slate-100 rounded-xl">
                <p className="text-sm text-slate-500">Selected Date</p>
                <p className="text-lg font-semibold text-slate-800">
                  {selectedDate?.toLocaleDateString('en-IN', { 
                    weekday: 'short',
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric' 
                  })}
                </p>
              </div>

              {/* Time Display */}
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-slate-800">
                  {String(selectedHour % 12 || 12).padStart(2, '0')}:{String(selectedMinute).padStart(2, '0')}
                  <span className="text-xl ml-2">{selectedHour >= 12 ? 'PM' : 'AM'}</span>
                </div>
              </div>

              {/* Hour Selection */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-500 block mb-2">Hour</label>
                <div className="grid grid-cols-6 gap-1 max-h-32 overflow-y-auto">
                  {hours.map((hour) => {
                    const hour12 = hour % 12 || 12
                    const ampm = hour >= 12 ? 'PM' : 'AM'
                    return (
                      <button
                        key={hour}
                        type="button"
                        onClick={() => setSelectedHour(hour)}
                        className={cn(
                          "p-2 rounded-lg text-xs font-medium transition-all touch-manipulation",
                          selectedHour === hour
                            ? "bg-slate-800 text-white"
                            : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                        )}
                      >
                        {hour12}{ampm}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Minute Selection */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-500 block mb-2">Minute</label>
                <div className="grid grid-cols-6 gap-1">
                  {minutes.map((min) => (
                    <button
                      key={min}
                      type="button"
                      onClick={() => setSelectedMinute(min)}
                      className={cn(
                        "p-2 rounded-lg text-sm font-medium transition-all touch-manipulation",
                        selectedMinute === min
                          ? "bg-slate-800 text-white"
                          : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                      )}
                    >
                      :{String(min).padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep('date')}
                  className="flex-1 py-3 px-4 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-colors touch-manipulation"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleTimeConfirm}
                  className="flex-1 py-3 px-4 rounded-xl bg-slate-800 text-white font-semibold hover:bg-slate-900 transition-colors touch-manipulation"
                >
                  Confirm
                </button>
              </div>
            </div>
          )}
        </div>
      </dialog>
    </>
  )
}
